/* Bumblebee design tokens.

   Ported from the Claude Design canvas "JS Course Mockups Bumblebee". The dark
   palette is the canvas's own.

   Three theme states: an explicit choice stamps data-theme on <html>; the
   default follows the operating system. Inverted surfaces (the active unit
   band, the NOTICE block) flip direction between themes rather than staying
   dark — that is the design's own logic, not an invention.
*/

:root {
  color-scheme: light;

  /* surfaces */
  /* A real page, not an artboard. #c9c9c6 is the design canvas's own backdrop —
     the grey a mockup is pinned to — and does not belong on the site. theme.js
     uses paper here too, so the content column reads as the page itself. */
  --page:       #f4f4f2;
  --paper:      #f4f4f2;
  --panel:      #e2e2df;
  --panel-2:    #ececea;
  --hover:      #e8e8e5;
  --card:       #ffffff;

  /* text */
  --ink:        #121212;
  --body:       #2e2e2e;
  --ink-72:     rgba(18, 18, 18, .72);
  --ink-68:     rgba(18, 18, 18, .68);
  --ink-55:     rgba(18, 18, 18, .55);
  --ink-45:     rgba(18, 18, 18, .45);
  --ink-30:     rgba(18, 18, 18, .30);
  --ink-16:     rgba(18, 18, 18, .16);
  --ink-12:     rgba(18, 18, 18, .12);
  --ink-08:     rgba(18, 18, 18, .08);

  /* hazard yellow */
  --accent:     #ffc400;
  --bright:     #ffd633;
  --lemon:      #ffcc4d;
  --olive:      #6b5000;
  --olive-2:    #7a5c00;
  --deep:       #4a3800;
  --gold:       #e8c96a;
  --orange:     #ff6b2c;

  /* Inverted surfaces — dark on a light page, light on a dark one.
     These are the canvas's own values. Do not substitute a generic neutral:
     a grey wash reads muddy where the design uses a specific tint. */
  --invert-bg:      #1a1a1a;
  --invert-fg:      #f4f4f2;
  --invert-dim:     rgba(244, 244, 242, .6);
  --invert-faint:   rgba(244, 244, 242, .4);
  --invert-rule:    rgba(255, 255, 255, .10);
  --invert-edge:    rgba(0, 0, 0, .4);
  --invert-surface: rgba(255, 255, 255, .04);
  --invert-code:    rgba(255, 255, 255, .14);
  --invert-label:   #ffd633;

  /* The mid-gold hairline on margin notes. Not --olive, which is far darker. */
  --gold-rule: rgba(163, 120, 0, .5);
  /* Inline code inside a light-tinted directive block. */
  --block-code: rgba(18, 18, 18, .07);

  /* code */
  --code-bg:    #1a1a1a;
  --code-fg:    #eaeaea;
  --code-kw:    #ffd633;
  --code-num:   #ff6b2c;
  --code-com:   #8f8f8f;
  --code-str:   #c8e6a0;

  /* block tints */
  --check-bg:   #eef3e8;
  /* Neutral, not yellow. The Try-it band used to be a pale yellow, which put
     it in the same hue as the brand accent AND as the correct/incorrect
     tints sitting on top of it — three warm pastels competing. */
  --try-bg:     #e9ecec;
  /* Correct is green, not the brand yellow. `--accent` means "current" or
     "next" everywhere else; using it for "right" made one hue mean two
     things on the same control. */
  --right:      #3f7d4e;
  /* Slate blue, the second colour beside yellow (Nick, 2026-09-28); sky in dark. */
  --slate:      #3f5f86;
  --wrong-bg:   #f7ece2;

  --rule:        rgba(18, 18, 18, .14);
  --rule-strong: #121212;

  /* type */
  --sans:  Karla, system-ui, sans-serif;
  --serif: Spectral, Georgia, serif;
  --mono:  'JetBrains Mono', ui-monospace, Menlo, monospace;

  /* The reading measure — the width of the text column itself, not just of
     paragraphs. The canvas specifies 62ch (~608px), but that was set inside a
     fixed 1160px artboard where it was all the room there was. On a real page
     with a nav rail on one side and margin notes on the other, 62ch leaves the
     text looking like a ribbon between two panels. 42rem (~672px, ~68ch) sits
     inside the comfortable range and balances the three columns.

     In rem, not ch: a `ch` in a grid track resolves against the grid
     container's font rather than the paragraph's, which silently sizes the
     column narrower than the text it is meant to hold. */
  --measure: 42rem;

  --radius-sm: 5px;
  --radius:    6px;
  --radius-lg: 8px;
  --radius-xl: 10px;
}

/* Shared dark values, applied to both the system default and the explicit choice. */
@media (prefers-color-scheme: dark) {
  :root:not([data-theme="light"]) {
    color-scheme: dark;

    --page:    #131313;
    --paper:   #161616;
    --panel:   #202020;
    --panel-2: #1b1b1b;
    --hover:   #262626;
    --card:    #1c1c1c;

    --ink:     #f4f4f2;
    --body:    #d4d4d0;
    --ink-72:  rgba(244, 244, 242, .72);
    --ink-68:  rgba(244, 244, 242, .68);
    --ink-55:  rgba(244, 244, 242, .55);
    --ink-45:  rgba(244, 244, 242, .45);
    --ink-30:  rgba(244, 244, 242, .30);
    --ink-16:  rgba(244, 244, 242, .16);
    --ink-12:  rgba(244, 244, 242, .12);
    --ink-08:  rgba(244, 244, 242, .08);

    --accent:  #ffd633;
    --bright:  #ffd633;
    --olive:   #e8c96a;
    --olive-2: #d4b455;
    --deep:    #ffd633;
    --orange:  #ff8a52;

    --invert-bg:      #f4f4f2;
    --invert-fg:      #121212;
    --invert-dim:     rgba(18, 18, 18, .62);
    --invert-faint:   rgba(18, 18, 18, .45);
    --invert-rule:    rgba(18, 18, 18, .14);
    --invert-edge:    rgba(18, 18, 18, .22);
    --invert-surface: rgba(18, 18, 18, .05);
    --invert-code:    rgba(18, 18, 18, .10);
    --invert-label:   #6b5000;

    --gold-rule: rgba(232, 201, 106, .45);
    --block-code: rgba(244, 244, 242, .10);

    --code-bg:  #0d0d0d;
    --code-num: #ff8a52;

    --check-bg: #1b2018;
    --try-bg:   #1a1d1d;
    --right:    #7bc48a;
    --slate:    #bcd3ea;
    --wrong-bg: #241a14;

    --rule:        rgba(244, 244, 242, .16);
    --rule-strong: #f4f4f2;
  }
}

:root[data-theme="dark"] {
  color-scheme: dark;

  --page:    #131313;
  --paper:   #161616;
  --panel:   #202020;
  --panel-2: #1b1b1b;
  --hover:   #262626;
  --card:    #1c1c1c;

  --ink:     #f4f4f2;
  --body:    #d4d4d0;
  --ink-72:  rgba(244, 244, 242, .72);
  --ink-68:  rgba(244, 244, 242, .68);
  --ink-55:  rgba(244, 244, 242, .55);
  --ink-45:  rgba(244, 244, 242, .45);
  --ink-30:  rgba(244, 244, 242, .30);
  --ink-16:  rgba(244, 244, 242, .16);
  --ink-12:  rgba(244, 244, 242, .12);
  --ink-08:  rgba(244, 244, 242, .08);

  --accent:  #ffd633;
  --bright:  #ffd633;
  --olive:   #e8c96a;
  --olive-2: #d4b455;
  --deep:    #ffd633;
  --orange:  #ff8a52;

  --invert-bg:      #f4f4f2;
  --invert-fg:      #121212;
  --invert-dim:     rgba(18, 18, 18, .62);
  --invert-faint:   rgba(18, 18, 18, .45);
  --invert-rule:    rgba(18, 18, 18, .14);
  --invert-edge:    rgba(18, 18, 18, .22);
  --invert-surface: rgba(18, 18, 18, .05);
  --invert-code:    rgba(18, 18, 18, .10);
  --invert-label:   #6b5000;

  --gold-rule: rgba(232, 201, 106, .45);
  --block-code: rgba(244, 244, 242, .10);

  --code-bg:  #0d0d0d;
  --code-num: #ff8a52;

  --check-bg: #1b2018;
  --try-bg:   #1a1d1d;
  --right:    #7bc48a;
  --slate:    #bcd3ea;
  --wrong-bg: #241a14;

  --rule:        rgba(244, 244, 242, .16);
  --rule-strong: #f4f4f2;
}
